// src/components/setup/steps/CourierAssignment.jsx
import React, { useState, useEffect } from 'react';
import { Truck, Plus, Check, Phone, Mail, Globe, MapPin } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { getAllCourierCompanies } from '../../../services/api/courierCompany';
import { assignCouriersToBusiness } from '../../../services/api/businessCourier';
import styles from './StepComponents.module.css';

const CourierAssignment = ({ user, formData, setFormData, onNext, onSkip, isSubmitting }) => {
  const { t } = useLocalization();
  const { success, error } = useAlert();

  const [availableCouriers, setAvailableCouriers] = useState([]);
  const [selectedCourierIds, setSelectedCourierIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);

  // Load available couriers
  useEffect(() => {
    fetchAvailableCouriers();
  }, []);

  const fetchAvailableCouriers = async () => {
    try {
      setIsLoading(true);
      const response = await getAllCourierCompanies();
      setAvailableCouriers(response.courierCompanies || []);
    } catch (err) {
      console.error('Failed to fetch available couriers:', err);
      error(t('setup.couriersLoadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleCourierSelection = (courierId) => {
    setSelectedCourierIds(prev => 
      prev.includes(courierId) 
        ? prev.filter(id => id !== courierId)
        : [...prev, courierId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCourierIds.length === availableCouriers.length) {
      setSelectedCourierIds([]);
    } else {
      setSelectedCourierIds(availableCouriers.map(courier => courier.id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedCourierIds.length === 0) {
      error(t('setup.selectAtLeastOneCourier'));
      return;
    }

    setIsAssigning(true);

    try {
      const result = await assignCouriersToBusiness(selectedCourierIds);
      
      // Update form data for next steps
      setFormData(prev => ({
        ...prev,
        assignedCouriers: result.created
      }));

      success(t('setup.couriersAssignedSuccess', { count: result.totalCreated }));
      onNext();
    } catch (err) {
      console.error('Courier assignment failed:', err);
      if (err.response?.data?.message) {
        error(err.response.data.message);
      } else {
        error(t('setup.courierAssignmentError'));
      }
    } finally {
      setIsAssigning(false);
    }
  };

  const handleSkipStep = () => {
    // Update form data to indicate step was skipped
    setFormData(prev => ({
      ...prev,
      couriersSkipped: true
    }));
    onSkip();
  };

  if (isLoading) {
    return (
      <div className={styles.stepContainer}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
          <h3>{t('setup.loadingCouriers')}</h3>
          <p>{t('setup.loadingCouriersDesc')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stepContainer}>
      <div className={styles.stepHeader}>
        <Truck className={styles.stepIcon} size={48} />
        <h2>{t('setup.courierAssignmentTitle')}</h2>
        <p>{t('setup.courierAssignmentDescription')}</p>
      </div>

      {availableCouriers.length === 0 ? (
        <div className={styles.emptyState}>
          <Truck size={48} className={styles.emptyIcon} />
          <h3>{t('setup.noCouriersAvailable')}</h3>
          <p>{t('setup.noCouriersDescription')}</p>
          
          <div className={styles.stepActions}>
            <button
              type="button"
              onClick={handleSkipStep}
              className={styles.secondaryButton}
            >
              {t('setup.skipForNow')}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className={styles.stepForm}>
          {/* Selection Controls */}
          <div className={styles.selectionControls}>
            <div className={styles.selectionInfo}>
              <span>{t('setup.selectedCouriers', { 
                selected: selectedCourierIds.length, 
                total: availableCouriers.length 
              })}</span>
            </div>
            <button
              type="button"
              onClick={handleSelectAll}
              className={styles.selectAllButton}
            >
              {selectedCourierIds.length === availableCouriers.length ? 
                t('setup.deselectAll') : 
                t('setup.selectAll')
              }
            </button>
          </div>

          {/* Courier Selection List */}
          <div className={styles.courierSelectionList}>
            {availableCouriers.map(courier => (
              <div
                key={courier.id}
                className={`${styles.courierSelectionItem} ${
                  selectedCourierIds.includes(courier.id) ? styles.selected : ''
                }`}
                onClick={() => handleToggleCourierSelection(courier.id)}
              >
                <div className={styles.courierCheckbox}>
                  <input
                    type="checkbox"
                    checked={selectedCourierIds.includes(courier.id)}
                    onChange={() => {}}
                    className={styles.checkbox}
                  />
                </div>
                
                <div className={styles.courierInfo}>
                  <div className={styles.courierName}>{courier.name}</div>
                  
                  {courier.description && (
                    <div className={styles.courierDescription}>
                      {courier.description}
                    </div>
                  )}
                  
                  {courier.contactInfo && (
                    <div className={styles.courierContact}>
                      {courier.contactInfo.phone && (
                        <span>
                          <Phone size={12} />
                          {courier.contactInfo.phone}
                        </span>
                      )}
                      {courier.contactInfo.email && (
                        <span>
                          <Mail size={12} />
                          {courier.contactInfo.email}
                        </span>
                      )}
                      {courier.contactInfo.website && (
                        <span>
                          <Globe size={12} />
                          {courier.contactInfo.website}
                        </span>
                      )}
                      {courier.contactInfo.address && (
                        <span>
                          <MapPin size={12} />
                          {courier.contactInfo.address}
                        </span>
                      )}
                    </div>
                  )}
                  
                  <div className={styles.courierFeatures}>
                    {courier.apiAllowed && (
                      <span className={styles.feature}>
                        <Check size={12} />
                        {t('setup.apiSupported')}
                      </span>
                    )}
                    {courier.webHookAllowed && (
                      <span className={styles.feature}>
                        <Check size={12} />
                        {t('setup.webhookSupported')}
                      </span>
                    )}
                    {courier.isAutoWaybill && (
                      <span className={styles.feature}>
                        <Check size={12} />
                        {t('setup.autoWaybill')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Information Box */}
          <div className={styles.infoBox}>
            <h4>{t('setup.courierInfoTitle')}</h4>
            <ul>
              <li>{t('setup.courierInfoPoint1')}</li>
              <li>{t('setup.courierInfoPoint2')}</li>
              <li>{t('setup.courierInfoPoint3')}</li>
              <li>{t('setup.courierInfoPoint4')}</li>
            </ul>
          </div>

          <div className={styles.stepActions}>
            <button
              type="button"
              onClick={handleSkipStep}
              disabled={isAssigning || isSubmitting}
              className={styles.secondaryButton}
            >
              {t('setup.skipForNow')}
            </button>
            
            <button
              type="submit"
              disabled={selectedCourierIds.length === 0 || isAssigning || isSubmitting}
              className={styles.primaryButton}
            >
              {isAssigning ? (
                <>
                  <div className={styles.loadingSpinner}></div>
                  {t('setup.assigning')}
                </>
              ) : (
                <>
                  <Plus size={20} />
                  {t('setup.assignCouriers')} ({selectedCourierIds.length})
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default CourierAssignment;